import { Fields } from "@cd2/forms"
import { P, TextBlock } from "@cd2/text/build/es6"
import React, { useState } from "react"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { IRecord } from "../../types/schema"

export type ISubCustomer = IRecord<"sub_customers", "name"> & {
  customer: IRecord<"customers", "name"> & { branch: IRecord<"branches", "name"> }
}

export function SubCustomerSelectList({
  value,
  onChange,
  label,
  disabled,
  branchId,
  customerId,
  filter,
  isClearable,
  includeBranchName,
  error,
  noBranchFilter,
  hideIfNone,
  userId,
  contactId,
  required,
  siblings,
}: {
  siblings?: (args?: { options?: ISubCustomer[] }) => React.ReactNode
  value?: string
  onChange(id?: string, customer?: ISubCustomer): void
  label?: string
  disabled?: boolean
  branchId?: string
  customerId?: string
  filter?: any
  isClearable?: boolean
  includeBranchName?: boolean
  error?: string
  noBranchFilter?: boolean
  hideIfNone?: boolean
  userId?: string
  contactId?: string
  required?: boolean
}) {
  const skeem = window.skeem
  const [isLoading, setIsLoading] = useState(true)

  const [records, setRecords] = useState<ISubCustomer[]>([])

  function handleChange(id?: string, customer?: ISubCustomer) {
    onChange(id, customer)
  }

  useAsyncEffect(async () => {
    try {
      const skeemFilter: any[] = [
        userId && {
          // has a contact user on this sub customer
          gte: [
            { count: { path: [{ name: "contacts", filter: { matches: { user: userId } } }] } },
            { value: 1 },
          ],
        },
        contactId && {
          // has a contact on this sub customer
          gte: [
            { count: { path: [{ name: "contacts", filter: { matches: { id: contactId } } }] } },
            { value: 1 },
          ],
        },
        {
          gt: [
            {
              count: {
                path: [
                  {
                    name: "customer",
                    filter: customerId ? { matches: { id: customerId } } : undefined,
                  },
                  // if branch id, filter by branch
                  !noBranchFilter && branchId
                    ? { name: "branch", filter: { matches: { id: branchId } } }
                    : undefined,
                  // if no branch id, filter by session branch
                  ...(!noBranchFilter && !branchId
                    ? ["branch", { name: "users", filter: { matches: { id: { session: true } } } }]
                    : []),
                ].filter(Boolean),
              },
            },
            { value: 0 },
          ],
        },
        filter,
      ].filter(Boolean)
      const records = await skeem.fetch2<ISubCustomer[]>({
        model: {
          name: "sub_customers",
          attributes: [
            "name",
            { name: "customer", attributes: ["name", { name: "branch", attributes: ["name"] }] },
          ],
          filter: { and: skeemFilter },
          sort: { by: "name", direction: "asc" },
        },
      })
      setRecords(records)
      console.log("GOT SUBS?", records, filter)
    } finally {
      setIsLoading(false)
    }
  }, [branchId, customerId])

  if (isLoading) {
    return <div>Loading</div>
  }

  if (!isLoading && hideIfNone && records.length === 0) {
    return null
  }

  return (
    <React.Fragment>
      <Fields.Select2
        label={label ?? "Sub Customer"}
        options={records?.map(record => ({ value: record.id, label: record.name }))}
        onChange={opt =>
          handleChange(
            opt?.value,
            records.find(subCustomer => subCustomer.id === opt?.value),
          )
        }
        value={(() => {
          const customer = records.find(v => v.id === value)
          if (customer) {
            return { value: customer.id, label: customer.name }
          }
          return undefined
        })()}
        disabled={disabled}
        isClearable={isClearable}
        showRequiredStar={required}
        error={error}
        formatOptionLabel={
          includeBranchName
            ? opt => {
                const subCustomer = records.find(v => v.id === opt.value)
                return (
                  <TextBlock blockSpacing={0}>
                    {subCustomer?.customer?.name && <P size="small">{subCustomer.customer.name}</P>}
                    <P size="small">{subCustomer?.name}</P>
                    {subCustomer?.customer?.branch?.name && (
                      <P size="small">{subCustomer.customer.branch.name}</P>
                    )}
                  </TextBlock>
                )
              }
            : undefined
        }
      />
      {siblings && siblings({ options: records })}
    </React.Fragment>
  )
}
